import { Location } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '../auth/services/auth.service';
import { NotificationService } from '../services/notification.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private notificationService: NotificationService,
    private authService: AuthService,
    private router: Router,
    public location: Location
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return from(this.authService.refreshAsync()).pipe(
            // Only a failed token refresh logs the user out here; failures of
            // the retried request are handled separately below.
            // Rethrow the original 401 rather than the Auth0 error so callers
            // always receive an HttpErrorResponse.
            catchError(() => {
              this.authService.logout().subscribe();
              return throwError(error);
            }),
            switchMap((token: any) => {
              if (token.accessToken) {
                const clonedRequest = req.clone({
                  setHeaders: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    Authorization: `Bearer ${token.accessToken}`,
                  },
                });
                return next.handle(clonedRequest).pipe(
                  catchError((retryError: HttpErrorResponse) => {
                    if (retryError.status === 401) {
                      this.authService.logout().subscribe();
                      return throwError(retryError);
                    }
                    return this.notifyAndRethrow(retryError);
                  })
                );
              } else {
                this.authService.logout().subscribe();
                return throwError(error);
              }
            })
          );
        }

        return this.notifyAndRethrow(error);
      })
    );
  }

  private notifyAndRethrow(error: HttpErrorResponse): Observable<never> {
    if (error instanceof HttpErrorResponse && error.status === 0) {
      const message =
        'The server is not available. Please try again later or contact an administrator';
      this.notificationService.show(message, { type: 'error' });
    } else if (error instanceof HttpErrorResponse) {
      const message =
        error.error?.errors?.length > 0
          ? error.error.errors[0]
          : 'Something went wrong. Please try again later or contact an administrator';
      this.notificationService.show(message, { type: 'error' });
    }
    return throwError(error);
  }
}
